import { createContext, useContext, useEffect, useState } from 'react';
import type React from 'react';
import type { ThemeName, ThemeContextValue } from './theme.types';
import { themeNames, defaultTheme } from './themes.config';
import { applyTheme, getSavedTheme, saveTheme } from './theme.utils';

/**
 * Context per il tema corrente
 */
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Props del ThemeProvider
 */
export interface ThemeProviderProps {
  /**
   * Tema iniziale (opzionale, usa localStorage se non specificato)
   */
  defaultTheme?: ThemeName;
  /**
   * Forza un tema specifico (non modificabile dall'utente)
   */
  forcedTheme?: ThemeName;
  /**
   * Figli
   */
  children: React.ReactNode;
  /**
   * Abilita persistenza in localStorage
   * @default true
   */
  enableSystem?: boolean;
}

/**
 * ThemeProvider Component
 * 
 * Provider per il sistema di temi. Gestisce il tema corrente,
 * lo applica al DOM e lo persiste in localStorage.
 * 
 * @example
 * ```tsx
 * <ThemeProvider defaultTheme="light">
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  defaultTheme: initialTheme,
  forcedTheme,
  children,
  enableSystem = true,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (forcedTheme) return forcedTheme;
    if (enableSystem && typeof window !== 'undefined') {
      return getSavedTheme();
    }
    return initialTheme || defaultTheme;
  });

  /**
   * Imposta un nuovo tema
   */
  const setTheme = (newTheme: ThemeName) => {
    if (forcedTheme) return; // Non permettere cambio se tema forzato
    
    setThemeState(newTheme);
    applyTheme(newTheme);
    
    if (enableSystem) {
      saveTheme(newTheme);
    }
  };

  /**
   * Applica il tema al mount e quando cambia
   */
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const value: ThemeContextValue = {
    theme,
    setTheme,
    themes: themeNames,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook per accedere al tema corrente
 * 
 * @returns Context value con tema corrente e setter
 * @throws Error se usato fuori da ThemeProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, setTheme } = useTheme();
 *   
 *   return (
 *     <button onClick={() => setTheme('dark')}>
 *       Tema corrente: {theme}
 *     </button>
 *   );
 * }
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  
  // Durante SSR (server-side rendering), restituisci valori di default
  if (typeof window === 'undefined') {
    return {
      theme: 'light' as ThemeName,
      setTheme: () => {},
      availableThemes: themeNames,
    };
  }
  
  if (context === undefined) {
    throw new Error('useTheme deve essere usato all\'interno di un ThemeProvider');
  }
  
  return context;
}

/**
 * Hook per ottenere solo il tema corrente (senza setter)
 * Utile per componenti che devono solo leggere il tema
 */
export function useCurrentTheme(): ThemeName {
  const { theme } = useTheme();
  return theme;
}

